import React from 'react';
import { Box, Flex, Heading, Text, VStack, Badge, useBreakpointValue, VisuallyHidden } from '@chakra-ui/react';
import {
  Search,
  Server,
  Plug,
  AlertTriangle,
  Workflow,
  BrainCircuit,
  GitBranch,
  Calendar,
  Users,
  UserCheck,
  Eye,
  CheckCircle
} from 'lucide-react';
import { useCleanupProgress } from '@/providers/cleanup-progress-provider';

const PAGERDUTY_GREEN = '#06AC38';
const PAGERDUTY_BLUE = '#0052cc';
const GRAY_DEFAULT = '#A0AEC0';

// Icon mapping for cleanup steps - Order from PRD STEP 0-10
const CLEANUP_STEP_ICONS: Record<string, React.ElementType> = {
  rapid_discovery: Search,
  services: Server,
  integrations: Plug,
  incidents: AlertTriangle,
  incident_workflows: Workflow,
  aiops: BrainCircuit,
  escalation_policies: GitBranch,
  schedules: Calendar,
  teams: Users,
  responders: UserCheck,
  stakeholders: Eye,
};

const getCleanupStepIcon = (stepName: string): React.ElementType => {
  return CLEANUP_STEP_ICONS[stepName] || CheckCircle;
};

interface CleanupProgressSidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

export const CleanupProgressSidebar: React.FC<CleanupProgressSidebarProps> = ({ isCollapsed, onToggle }) => {
  const { steps, currentStep, issuesFound, issuesResolved, scopeSelected } = useCleanupProgress();

  // Force collapsed on mobile, respect prop on desktop
  const isMobile = useBreakpointValue({ base: true, md: false });
  const forceCollapsed = isMobile ? true : isCollapsed;

  // Calculate progress percentage
  const completedCount = steps.filter((step) => step.status === 'Complete').length;
  const progressPercentage = steps.length > 0 ? Math.round((completedCount / steps.length) * 100) : 0;

  return (
    <Box
      w={{ base: '50px', md: forceCollapsed ? '60px' : '280px' }}
      minW={{ base: '50px', md: '100px' }}
      h="100%"
      bg="white"
      borderRight="1px solid"
      borderColor="gray.200"
      position="relative"
      transition="all 0.3s ease-in-out"
      display="flex"
      flexDirection="column"
    >
      {/* Scrollable Content Area */}
      <Box
        flex="1"
        overflowY="auto"
        overflowX="hidden"
        p={{ base: 2, md: forceCollapsed ? 2 : 6 }}
      >
        {forceCollapsed ? (
          /* Collapsed State */
          <VStack spacing={0} align="center" pt={4}>
            {/* Progress Badge */}
            <Badge
              colorScheme="green"
              fontSize="sm"
              fontWeight="bold"
              borderRadius="full"
              px={4}
              py={1}
              minW="44px"
              textAlign="center"
              mb={4}
            >
              {progressPercentage}%
            </Badge>

            {/* Vertical Timeline Icons */}
            <VStack spacing={0} align="center">
              {steps.map((step, index) => {
                const isCurrentStep = currentStep?.step_name === step.step_name;
                const isCompleted = step.status === 'Complete';
                const isLastStep = index === steps.length - 1;
                const StepIcon = getCleanupStepIcon(step.step_name);

                // Determine icon color based on state
                const iconColor = isCompleted
                  ? PAGERDUTY_GREEN
                  : isCurrentStep
                    ? PAGERDUTY_BLUE
                    : GRAY_DEFAULT;

                const stepStatus = isCompleted ? 'completed' : isCurrentStep ? 'current step' : 'not started';

                return (
                  <Box key={step.step_name} position="relative">
                    <Box py={4}>
                      <StepIcon size={20} color={iconColor} aria-hidden="true" />
                      <VisuallyHidden>{step.display_name}: {stepStatus}</VisuallyHidden>
                    </Box>

                    {/* Vertical connecting line */}
                    {!isLastStep && (
                      <Box
                        position="absolute"
                        left="50%"
                        top="28px"
                        my={4}
                        w="2px"
                        h="16px"
                        bg={isCompleted ? PAGERDUTY_GREEN : 'gray.200'}
                        transform="translateX(-50%)"
                      />
                    )}
                  </Box>
                );
              })}
            </VStack>
          </VStack>
        ) : (
          /* Expanded State */
          <>
            <Heading size="md" mb={4}>
              Optimize & Cleanup Progress
            </Heading>

            {/* Progress Bar */}
            <Box mb={4}>
              <Flex justify="space-between" align="center" mb={2}>
                <Badge
                  colorScheme="green"
                  fontSize="sm"
                  fontWeight="bold"
                  borderRadius="full"
                  px={6}
                  py={1}
                  minW="44px"
                  textAlign="center"
                >
                  {progressPercentage}% Complete
                </Badge>
              </Flex>
            </Box>

            {/* Issues Summary */}
            <Box mb={4} p={3} backgroundColor="orange.50" borderRadius="md">
              <Flex justify="space-between" mb={1}>
                <Text fontSize="xs" color="gray.700">
                  Issues Found:
                </Text>
                <Text fontSize="sm" color="orange.700" fontWeight="bold">
                  {issuesFound}
                </Text>
              </Flex>
              <Flex justify="space-between">
                <Text fontSize="xs" color="gray.700">
                  Issues Resolved:
                </Text>
                <Text fontSize="sm" color="green.700" fontWeight="bold">
                  {issuesResolved}
                </Text>
              </Flex>
            </Box>

            {/* Getting Started Info - Show contextual message based on state */}
            {progressPercentage === 0 && (
              <Box mb={4} p={3} backgroundColor="blue.50" borderRadius="md" borderLeft="4px solid" borderLeftColor="blue.500">
                <Text fontSize="sm" fontWeight="semibold" color="blue.800" mb={1}>
                  {scopeSelected ? 'Ready to Begin' : 'Getting Started'}
                </Text>
                <Text fontSize="xs" color="blue.700">
                  {scopeSelected
                    ? "Your analysis scope is set. Let's start with the first step!"
                    : "Tell me which team you'd like to analyze. After that, you'll choose which areas to review."}
                </Text>
              </Box>
            )}

            {/* Steps List */}
            <VStack align="stretch" spacing={0}>
              {steps.map((step, index) => {
                const isCurrentStep = currentStep?.step_name === step.step_name;
                const isCompleted = step.status === 'Complete';
                const isLastStep = index === steps.length - 1;
                const StepIcon = getCleanupStepIcon(step.step_name);

                // Determine icon color based on state
                const iconColor = isCompleted
                  ? PAGERDUTY_GREEN
                  : isCurrentStep
                    ? PAGERDUTY_BLUE
                    : GRAY_DEFAULT;

                const stepStatus = isCompleted ? 'completed' : isCurrentStep ? 'current step' : 'not started';

                return (
                  <Box key={step.step_name} position="relative">
                    <Flex align="center" gap={3} py={2}>
                      <Box position="relative" py={2} flexShrink={0}>
                        <StepIcon size={20} color={iconColor} aria-hidden="true" />
                        <VisuallyHidden>{step.display_name}: {stepStatus}</VisuallyHidden>
                      </Box>

                      <Box flex={1}>
                        <Text
                          fontSize="sm"
                          fontWeight={isCurrentStep ? 'semibold' : 'normal'}
                          color={isCompleted || isCurrentStep ? 'gray.800' : 'gray.500'}
                        >
                          {step.display_name}
                        </Text>
                        {isCurrentStep && step.issues_found > 0 && (
                          <Text fontSize="xs" color="orange.600">
                            {step.issues_found} issues found
                          </Text>
                        )}
                      </Box>
                    </Flex>

                    {/* Vertical connecting line */}
                    {!isLastStep && (
                      <Box
                        position="absolute"
                        left="10px"
                        top="28px"
                        my={4}
                        w="2px"
                        h="16px"
                        bg={isCompleted ? PAGERDUTY_GREEN : 'gray.200'}
                        transform="translateX(-50%)"
                      />
                    )}
                  </Box>
                );
              })}
            </VStack>

            {/* Additional Info */}
            <Box mt={6} pt={4} borderTop="1px solid" borderColor="gray.200">
              <Text fontSize="xs" color="gray.600">
                Each step follows the flow: Discover → Prioritize → Clean up → Celebrate → Optimize → Expand
              </Text>
            </Box>
          </>
        )}
      </Box>

      {/* Toggle Button - Hidden on mobile */}
      <Box
        position="absolute"
        top="50%"
        right={0}
        transform="translateY(-50%)"
        zIndex={10}
        display={{ base: 'none', md: 'block' }}
      >
        <Box
          as="button"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          onClick={onToggle}
          w="24px"
          h="60px"
          bg="white"
          borderRadius="8px 0 0 8px"
          cursor="pointer"
          display="flex"
          alignItems="center"
          justifyContent="center"
          _hover={{ bg: 'gray.50' }}
          _active={{ bg: 'gray.100' }}
          transition="all 0.2s"
        >
          <Box w="2px" h="24px" bg="gray.400" borderRadius="full" />
        </Box>
      </Box>
    </Box>
  );
};
